import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { syncBrand } from '@shared/utils/brandSync';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  setDarkMode: (dark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      const saved = localStorage.getItem('wm_theme');
      return saved === 'dark';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = window.document.documentElement;
    const body = window.document.body;

    // Sincroniza o branding dinâmico (título, favicons, manifest, theme-color)
    syncBrand(isDarkMode);

    // Create a transient stylesheet to smoothly animate all element styles during the theme toggle
    const styleId = 'theme-transition-animation';
    let styleElement = document.getElementById(styleId);
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.innerHTML = `
        *, *::before, *::after {
          transition-property: color, background-color, border-color, text-decoration-color, fill, stroke !important;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1) !important;
          transition-duration: 200ms !important;
        }
      `;
      document.head.appendChild(styleElement);
    }

    root.classList.toggle('dark', isDarkMode);
    body.classList.toggle('dark', isDarkMode);
    
    try {
      localStorage.setItem('wm_theme', isDarkMode ? 'dark' : 'light');
    } catch {}

    // Safely tear down the transient style after the crossfade ends to prevent scroll/drag performance degradation
    const timer = setTimeout(() => {
      styleElement?.remove();
    }, 280);

    return () => clearTimeout(timer);
  }, [isDarkMode]);

  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  const setDarkMode = useCallback((dark: boolean) => {
    setIsDarkMode(dark);
  }, []);

  const contextValue = useMemo(() => ({
    isDarkMode,
    toggleTheme,
    setDarkMode
  }), [isDarkMode, toggleTheme, setDarkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
